import {
  errorISBN,
  errorISBNFormat,
  errorSourceName,
  inputAuthor,
  inputISBN,
  inputNome,
} from "_constants";
import {
  SelectTopicForSource,
  TextInput,
  UploadSource,
} from "components/shared";
import { InputText } from "primereact/inputtext";
import { useEffect, useRef, useState } from "react";
import {
  Control,
  Controller,
  UseFormGetFieldState,
  UseFormGetValues,
  UseFormSetValue,
  UseFormTrigger,
} from "react-hook-form";
import { checkIsbn, checkName } from "services";
import {
  setAddISBNNameValidation,
  setAddSourceNameValidation,
} from "store/slicer/FormAddSourceValidationSlice";
import { useAppDispatch, useAppSelector } from "store/store";
import { isbnRegex } from "utils";

export const FormSource = ({
  control,
  getValues,
  trigger,
  setValue,
  projectId,
  isPrivate = false,
}: {
  control: Control<
    {
      fileId: string;
      name: string;
      author: string;
      isbn: string;
      topicId: string;
    },
    any
  >;
  getValues: UseFormGetValues<{
    fileId: string;
    name: string;
    author: string;
    isbn: string;
    topicId: string;
  }>;
  trigger: UseFormTrigger<{
    fileId: string;
    name: string;
    author: string;
    isbn: string;
    topicId: string;
  }>;
  setValue: UseFormSetValue<{
    fileId: string;
    name: string;
    author: string;
    isbn: string;
    topicId: string;
  }>;
  getFieldState: UseFormGetFieldState<{
    fileId: string;
    name: string;
    author: string;
    isbn: string;
    topicId: string;
  }>;
  projectId?: string;
  isPrivate?: boolean;
}) => {
  const isSourceISBNValid = useAppSelector(
    (state) => state.formAddSourceValidationSlice.addSourceISBNIsValid
  );
  const isNameSourceValid = useAppSelector(
    (state) => state.formAddSourceValidationSlice.addSourceNameIsValid
  );
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const [prevName, setPrevName] = useState<string>("");
  const [prevIsbn, setPrevIsbn] = useState<string>("");
  const dispatch = useAppDispatch();

  const validationRules = {
    nameSourceCheck: () => {
      if (!isNameSourceValid) return errorSourceName;
      return true;
    },
  };

  const resetRegisterValidationStatus = () => {
    dispatch(setAddISBNNameValidation(true));
  };

  const handleFocusOnNameInput = () => {
    if (nameInputRef.current && getValues("name")) {
      nameInputRef.current.focus();
    }
    trigger("name");
    dispatch(setAddSourceNameValidation(true));
  };

  useEffect(() => {
    if (trigger && control._getDirty()) {
      trigger("isbn");
    }
    if (!isNameSourceValid) {
      trigger("name");
    }
  }, [isSourceISBNValid, trigger, control, isNameSourceValid]);

  useEffect(() => {
    if (getValues("isbn")?.length) {
      setPrevIsbn(getValues("isbn"));
    }
    if (getValues("name")?.length) {
      setPrevName(getValues("name"));
    }
    dispatch(setAddSourceNameValidation(true));
    dispatch(setAddISBNNameValidation(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const nameValidation = (name: string) => {
    if (name !== "" && name !== prevName) {
      checkName(name)
        .then((_) => {})
        .catch((error) => {
          if (error?.response?.status === 409) {
            dispatch(setAddSourceNameValidation(false));
          }
        });
    }
  };

  const isbnValidation = (isbn: string) => {
    if (isbn !== "" && isbn !== prevIsbn) {
      checkIsbn(isbn)
        .then((_) => {})
        .catch((error) => {
          if (error?.response?.status === 409) {
            dispatch(setAddISBNNameValidation(false));
          }
        });
    }
  };

  return (
    <>
      <div className="flex-container gap-8 column">
        <div className="width-100 margin-bottom-10">
          <UploadSource
            setValue={setValue}
            getValues={getValues}
            onFocusName={() => handleFocusOnNameInput()}
            control={control}
            projectId={projectId}
          />
        </div>
        <div className="width-100">
          <TextInput
            control={control}
            name="name"
            placeholder={inputNome}
            onlyLetters={false}
            ref={nameInputRef}
            focusOutFunction={nameValidation}
            validationRules={validationRules}
          />
        </div>
        <div className="flex-container gap-8">
          <div className="width-50">
            <TextInput
              control={control}
              name="author"
              placeholder={inputAuthor}
              required={false}
            />
          </div>
          <div className="width-50">
            <Controller
              name="isbn"
              control={control}
              rules={{
                validate: {
                  isbnSourceCheck: () => {
                    if (!isSourceISBNValid) return errorISBN;
                    return true;
                  },
                  onlyNumber: (value) =>
                    getValues("isbn")?.length === 0 ||
                    isbnRegex.test(value) ||
                    errorISBNFormat,
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <div className="flex-container column">
                  <InputText
                    id={field.name}
                    {...field}
                    className={error ? "p-invalid margin-bottom-4" : ""}
                    placeholder={inputISBN}
                    onFocus={resetRegisterValidationStatus}
                    onBlur={() => {
                      if (!error) {
                        isbnValidation(field.value);
                      }
                    }}
                  />

                  {error && (
                    <small className="p-error margin-bottom-4">
                      {error.message}
                    </small>
                  )}
                </div>
              )}
            />
          </div>
        </div>
      </div>
      <div className="flex-container margin-top-18">
        <div className="width-100">
          <SelectTopicForSource
            control={control}
            setValue={setValue}
            isRequired={!isPrivate}
          />
        </div>
      </div>
    </>
  );
};
